import { FunctionComponent as F, useEffect, useState } from "react";
import { useSelector } from 'react-redux';
import { DosingCosts, ListingInfo } from "@protoplan/types";
import { IState } from "../../core/store/store";
import Stack from '@mui/material/Stack';
import { RowGroup as RowGroup } from "./CostBreakdownRowGroup";
import CostBreakdownDescr from "./CostBreakdownDescr";
import CostBreakdownTable from "./CostBreakdownTable";
import UpdateTax from "../UI/Button/UpdateTax";
import { formUnitCostRows } from "./unitCostRows";
import units from "../../core/units";



interface IProps
{
  rows: (ListingInfo & DosingCosts)[],
  saving?: number;
  savingType: string;
}


const RowsCostBreakdown: F<IProps> = (props: IProps) =>
{
  const user = useSelector((state: IState) => state.user);

  if(!units)
    return <></>;

  const rows0 = props.rows;rows0

  const [rowsWithUnits, setRowsWithUnits] = useState<(
    ListingInfo &
    DosingCosts &
    { doseUnit?: string })[]>([]);

  useEffect(() =>
  {
    Promise.all(rows0.map(async r => await Promise.all([units.getUnit(r.doseUnitId), units.getUnit(r.amountUnitId)])
      .then(units => ({ ...r, doseUnit: units[0]?.name, amountUnit: String(units[1]?.name) }))))
      .then(rs => setRowsWithUnits(rs));
  }, []);

  const productName = (row: {
    bundleId?: number,
    listingName: string,
    productName: string }) => (row.bundleId ? row.listingName : row.productName);

  const fees = (row: { costPerMonthWithFees: number, costPerMonthWithoutFees: number }) =>
    row.costPerMonthWithFees - row.costPerMonthWithoutFees;

  let rowGroups: RowGroup[] = [
    // Unit cost
    {
      item: 'Unit prices',
      rows: rowsWithUnits.map(row => formUnitCostRows(row, user.currencySymbol, true)).flat() },
    // Listings per month
    {
      item: 'Units per month (at current doses)',
      value: '',
      rows: rowsWithUnits.map(row => ([
        {
          item: 'Dose per month of ' + productName(row),
          value: (row.dose * row.dosesPerDay * row.daysPerMonth).toPrecision(3) + ' ' + row.doseUnit },
        {
          item:
            'Unit conversion factor for ' + productName(row) + ' ' +
           '(' + row.doseUnit + '->' + row.amountUnit + ')',
          value: '*' + units.getFactor(row.doseUnitId, row.amountUnitId, [row.productId]),
          tooltip: ['Multiplication factor to convert ' + row.doseUnit + ' to ' + row.amountUnit],
          show: Boolean(row.doseUnitId !== row.amountUnitId) },
        { item: 'Amount per product', value: row.amount + ' ' + row.amountUnit},
        {
          item: 'Number in bundle',
          value: row.quantity,
          show: Boolean(row.bundleId) },
        {
          item: 'Units per month of ' + productName(row),
          value: row.listingsPerMonth.toFixed(1) }])).flat() },
    // Fees
    {
      item: 'Fees per month',
      value: user.currencySymbol + " " + rowsWithUnits.reduce((sum, r) => sum + fees(r), 0).toFixed(2),
      show: Boolean(rowsWithUnits.some(r => fees(r))),
      rows: rowsWithUnits.filter(r => fees(r)).map(r => ([
        {
          item: 'Basket limit' + (r.basketLimitEstimated ? ' (est.)' : ''),
          value: user.currencySymbol + " " + r.basketLimit.toFixed(0) },
        {
          item: 'Max. units per order' + (r.basketLimitEstimated ? ' (est.)' : ''),
          value: r.maxListingsPerOrder },
        { item: 'Orders per month', value: r.ordersPerMonth.toFixed(1) },
        {
          item: 'Delivery' + (r.deliveryPriceEstimated ? ' (est.)' : ''),
          value: r.listingCurrencySymbol + " " + r.deliveryPrice,
          show: Boolean(r.deliveryPrice) },
        { item: 'Tax' + (r.taxEstimated ? ' (est.)' : ''),
          value:
            user.currencySymbol + " " + (r.baseTax || 0).toFixed(2) +
            (r.taxPercent ? " + " + r.taxPercent + "%" : ""),
          valueSymbol: <UpdateTax listing={r} vendorId={r.vendorId} size={13}/>,
          tooltip:
            ['Fixed baseline tax added to each order,' +
            (r.taxPercent ? ' in addition to the % tax charged on the product price' : '')],
          show: Boolean(r.baseTax || r.taxPercent) }])).flat() },
    // Cost per month
    {
      item: 'Cost per month',
      value: user.currencySymbol + " " +
        rowsWithUnits.reduce((sum, r) => sum + r.costPerMonthWithFees, 0).toFixed(2),
      tooltip: ['Including fees'],
      bold: true }];

  if(props.saving)
  {
    rowGroups.push({
      item: 'Saving',
      value: user.currencySymbol + " " + props.saving.toFixed(2),
      tooltip: ["Difference between this and the current listing's cost per month"],
      bold: true,
      valueColor: 'green' });
  }

  return (
    <Stack spacing={1.5} sx={{ width: '100%' }}>
      <CostBreakdownDescr rows={rowsWithUnits} savingType={props.savingType} />
      <CostBreakdownTable rowGroups={rowGroups} saving={props.saving}/>
    </Stack>);
};

export default RowsCostBreakdown;
